import { useState, type FormEvent } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckSquare, Gamepad2, Plus, Sparkles, Square, X } from "lucide-react";
import { CUSTOM_NAME_MAX, isCustomAppId, type CommonGame } from "@/lib/api";
import { cn } from "@/lib/utils";

interface GameListProps {
  games: CommonGame[];
  disabled: Set<number>;
  highlightAppId?: number;
  onToggle: (appId: number, disabled: boolean) => void;
  onBulkToggle: (disabled: boolean) => void;
  onAddCustom: (name: string) => void;
  onRemoveCustom: (appId: number) => void;
  locked?: boolean;
}

export function GameList({
  games,
  disabled,
  highlightAppId,
  onToggle,
  onBulkToggle,
  onAddCustom,
  onRemoveCustom,
  locked = false,
}: GameListProps) {
  const enabledCount = games.length - disabled.size;
  const [customName, setCustomName] = useState("");

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (locked) return;
    const trimmed = customName.trim();
    if (trimmed.length === 0) return;
    onAddCustom(trimmed);
    setCustomName("");
  }

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Games everyone owns</span>
          <span className="text-sm font-normal text-muted-foreground">
            {enabledCount}/{games.length}
          </span>
        </CardTitle>
        {locked && (
          <p className="text-xs text-muted-foreground">
            Locked while the wheel is spinning…
          </p>
        )}
        {games.length > 0 && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onBulkToggle(false)}
              disabled={locked || disabled.size === 0}
              className="flex-1"
            >
              Enable all
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onBulkToggle(true)}
              disabled={locked || enabledCount === 0}
              className="flex-1"
            >
              Disable all
            </Button>
          </div>
        )}
        <form onSubmit={handleAdd} className="flex gap-2 pt-1">
          <Input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="Add a custom entry…"
            maxLength={CUSTOM_NAME_MAX}
            aria-label="Custom entry name"
            disabled={locked}
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            disabled={locked || customName.trim().length === 0}
          >
            <Plus />
            Add
          </Button>
        </form>
      </CardHeader>
      <CardContent>
        {games.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Once everyone in the room syncs their library, the games you all own will
            appear here. You can also add custom entries above.
          </p>
        ) : (
          <ScrollArea className="h-96 pr-2">
            <ul className="flex flex-col gap-1">
              {games.map((g) => {
                const isDisabled = disabled.has(g.appId);
                const isHighlight = g.appId === highlightAppId;
                const isCustom = g.custom ?? isCustomAppId(g.appId);
                return (
                  <li key={g.appId} className="group flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onToggle(g.appId, !isDisabled)}
                      disabled={locked}
                      className={cn(
                        "flex flex-1 items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-sm transition-colors",
                        isHighlight && "border-primary/40 bg-primary/10 text-primary",
                        !isHighlight && !locked && "hover:border-border/60 hover:bg-card/40",
                        isDisabled && "opacity-50",
                        locked && "cursor-not-allowed",
                      )}
                      title={
                        locked
                          ? "Locked while the wheel is spinning"
                          : isDisabled
                            ? "Click to enable"
                            : "Click to disable"
                      }
                    >
                      {isDisabled ? (
                        <Square className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : (
                        <CheckSquare className="h-4 w-4 shrink-0 text-primary" />
                      )}
                      {isCustom ? (
                        <Sparkles
                          className={cn(
                            "h-4 w-4 shrink-0",
                            isDisabled ? "text-muted-foreground" : "text-amber-400",
                          )}
                        />
                      ) : g.iconUrl ? (
                        <img
                          src={g.iconUrl}
                          alt=""
                          loading="lazy"
                          className={cn(
                            "h-5 w-5 rounded-sm object-cover",
                            isDisabled && "grayscale",
                          )}
                        />
                      ) : (
                        <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span
                        className={cn(
                          "truncate",
                          isDisabled && "line-through text-muted-foreground",
                        )}
                      >
                        {g.name}
                      </span>
                    </button>
                    {isCustom && (
                      <button
                        type="button"
                        onClick={() => onRemoveCustom(g.appId)}
                        disabled={locked}
                        title={
                          locked
                            ? "Locked while the wheel is spinning"
                            : "Remove custom entry"
                        }
                        aria-label={`Remove ${g.name}`}
                        className={cn(
                          "rounded-md p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100",
                          !locked && "hover:bg-destructive/10 hover:text-destructive",
                          locked && "cursor-not-allowed",
                        )}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
